import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, Download, CheckCircle2, AlertTriangle, ArrowLeft } from "lucide-react";

const CSV_HEADERS = ["title", "design_code", "sku", "sub_category", "labels", "metal", "purity", "weight_grams", "net_weight", "gross_weight", "making_charge", "pricing_mode", "base_price", "stone_details", "tags", "description"];

const SAMPLE_CSV = `title,design_code,sku,sub_category,labels,metal,purity,weight_grams,net_weight,gross_weight,making_charge,pricing_mode,base_price,stone_details,tags,description
Antique Gold Chain,TARU-CHN-101,SKU-101,Chain,"New;Bestseller",gold,22K,18.5,18.5,19,3500,gold-rate-based,0,,traditional;chain,Hand-crafted antique chain
Diamond Studs,TARU-ERR-102,SKU-102,Studs,"Diamond;Gift",gold,18K,3.2,3.0,3.2,2500,fixed,42000,0.5ct SI-GH,diamond;stud,Elegant everyday studs`;

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (!lines.length) return { rows: [], errors: ["Empty file"] };
  const parseRow = (line) => {
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"' && line[i-1] !== "\\") { inQ = !inQ; continue; }
      if (c === "," && !inQ) { out.push(cur); cur = ""; continue; }
      cur += c;
    }
    out.push(cur);
    return out.map(s => s.trim());
  };
  const headers = parseRow(lines[0]).map(h => h.toLowerCase());
  const rows = [];
  const errors = [];
  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue;
    const cells = parseRow(lines[i]);
    const row = {};
    headers.forEach((h, j) => { row[h] = cells[j] || ""; });
    if (!row.title) { errors.push(`Row ${i+1}: title required`); continue; }
    rows.push({
      title: row.title,
      design_code: row.design_code || null,
      sku: row.sku || null,
      sub_category: row.sub_category || null,
      labels: (row.labels || "").split(";").map(x => x.trim()).filter(Boolean),
      metal: row.metal || "gold",
      purity: row.purity || null,
      weight_grams: row.weight_grams ? Number(row.weight_grams) : null,
      net_weight: row.net_weight ? Number(row.net_weight) : null,
      gross_weight: row.gross_weight ? Number(row.gross_weight) : null,
      making_charge: row.making_charge ? Number(row.making_charge) : 0,
      pricing_mode: row.pricing_mode || "gold-rate-based",
      base_price: row.base_price ? Number(row.base_price) : 0,
      stone_details: row.stone_details || null,
      tags: (row.tags || "").split(";").map(x => x.trim()).filter(Boolean),
      description: row.description || null,
    });
  }
  return { rows, errors };
}

export default function ProductBulkAddPage() {
  const nav = useNavigate();
  const [text, setText] = useState("");
  const [parsed, setParsed] = useState({ rows: [], errors: [] });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!text.trim()) { setParsed({ rows: [], errors: [] }); return; }
    setParsed(parseCSV(text));
  }, [text]);

  const upload = async (file) => {
    const t = await file.text();
    setText(t);
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "taru-products-sample.csv";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const submit = async () => {
    if (!parsed.rows.length) return toast.error("Nothing to import");
    setBusy(true);
    try {
      const { data } = await api.post("/products/bulk", { items: parsed.rows });
      setResult(data);
      toast.success(`Imported ${data.created} product(s)`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="bulk-add-page">
      <button onClick={() => nav(-1)} className="text-sm text-[#4A5A50] hover:text-[#0F1E15] inline-flex items-center gap-1"><ArrowLeft size={14}/> Back</button>
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Catalog</div>
        <h1 className="font-serif text-3xl lg:text-4xl font-medium">Bulk Add Products</h1>
        <p className="text-[#4A5A50] mt-1">Import multiple products via CSV. Existing design codes will be skipped.</p>
      </div>

      <Card className="border-[#E4DFCF] rounded-xl">
        <CardContent className="p-6 space-y-4">
          <div className="flex flex-wrap gap-2">
            <label className="cursor-pointer">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0F5E39] text-white hover:bg-[#083F22] text-sm">
                <Upload size={14}/> Upload CSV
              </div>
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e)=>e.target.files?.[0] && upload(e.target.files[0])} data-testid="csv-upload-input"/>
            </label>
            <Button variant="outline" onClick={downloadSample} className="rounded-full" data-testid="csv-sample-btn">
              <Download size={14} className="mr-1"/> Sample CSV
            </Button>
          </div>

          <div>
            <Label>Or paste CSV content</Label>
            <Textarea rows={8} value={text} onChange={(e)=>setText(e.target.value)} className="mt-1 font-mono text-xs" placeholder={SAMPLE_CSV} data-testid="csv-textarea"/>
          </div>

          <div className="text-xs text-[#4A5A50]">
            <strong>Expected columns:</strong> {CSV_HEADERS.join(", ")}. Use `;` inside labels/tags to separate multiple values.
          </div>

          {parsed.rows.length > 0 && (
            <div className="p-3 bg-[#E1F0E6] text-[#0F5E39] rounded-lg text-sm flex items-center gap-2" data-testid="csv-preview-status">
              <CheckCircle2 size={16}/> {parsed.rows.length} row(s) parsed and ready to import.
            </div>
          )}
          {parsed.errors.length > 0 && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <div className="flex items-center gap-2 font-medium"><AlertTriangle size={14}/> {parsed.errors.length} row error(s)</div>
              <ul className="mt-2 space-y-0.5 list-disc list-inside">{parsed.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={submit} disabled={!parsed.rows.length || busy} className="bg-[#0F5E39] hover:bg-[#083F22] text-white rounded-full" data-testid="bulk-import-submit">
              {busy ? "Importing…" : `Import ${parsed.rows.length} product(s)`}
            </Button>
          </div>
        </CardContent>
      </Card>

      {parsed.rows.length > 0 && (
        <Card className="border-[#E4DFCF] rounded-xl">
          <CardContent className="p-0 overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-[#F1EDDF] text-[10px] uppercase tracking-widest text-[#4A5A50]">
                <tr>
                  <th className="px-3 py-2 text-left">Design Code</th>
                  <th className="px-3 py-2 text-left">Title</th>
                  <th className="px-3 py-2 text-left">Metal</th>
                  <th className="px-3 py-2 text-right">Weight (g)</th>
                  <th className="px-3 py-2 text-left">Labels</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E4DFCF]">
                {parsed.rows.slice(0, 25).map((r, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 font-medium">{r.design_code || "—"}</td>
                    <td className="px-3 py-2">{r.title}</td>
                    <td className="px-3 py-2">{r.metal} {r.purity}</td>
                    <td className="px-3 py-2 text-right">{r.weight_grams ?? r.net_weight ?? "—"}</td>
                    <td className="px-3 py-2">{r.labels.join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {parsed.rows.length > 25 && <div className="px-3 py-2 text-xs text-[#4A5A50] text-center">+ {parsed.rows.length - 25} more</div>}
          </CardContent>
        </Card>
      )}

      {result && (
        <Card className="border-[#E4DFCF] rounded-xl">
          <CardContent className="p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A50]">Result</div>
            <div className="font-serif text-2xl mt-1 text-[#0F5E39]">Imported {result.created} products</div>
            {result.skipped > 0 && <div className="text-sm text-[#4A5A50] mt-1">{result.skipped} skipped (duplicate design_code)</div>}
            {result.errors?.length > 0 && (
              <div className="mt-3 text-sm text-red-700">
                <div className="font-medium">Errors:</div>
                <ul className="list-disc list-inside">{result.errors.map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}</ul>
              </div>
            )}
            <Button className="mt-4 rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" onClick={() => nav("/products")} data-testid="back-to-products">Back to Products</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
